import { Edit, InfoOutline } from '@mui/icons-material';
import {
  Box,
  IconButton,
  styled,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC, PropsWithChildren, ReactNode } from 'react';
import { Column, Row } from '../Layout';

type FormFieldSize = 'sm' | 'md' | 'lg';

const sizesMap: Record<FormFieldSize, string> = {
  sm: '220px',
  md: '400px',
  lg: '500px',
};

const EditButton = styled(IconButton)({ padding: 0 });
const EditIcon = styled(Edit)({
  width: 16,
  height: 16,
});

const ColumnWrapper = styled(Column, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: FormFieldSize }>(({ size }) => ({
  width: '100%',
  maxWidth: size ? sizesMap[size] : '100%',
}));

const RowWrapper = styled(Row, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size?: FormFieldSize }>(({ size }) => ({
  width: '100%',
  maxWidth: size ? sizesMap[size] : '100%',
  alignItems: 'center',

  '& > div:first-child': {
    flexShrink: 0,
    flexBasis: 'auto',
  },
}));

const ChildrenWrapper = styled(Box)({
  width: '100%',
});

interface DotProps {
  color: string;
}
const Dot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'color',
})<DotProps>(({ theme, color }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: color,
  marginLeft: theme.spacing(1),
}));

type Props = {
  label?: string | ReactNode;
  tooltip?: string;
  extraInformation?: string;
  size?: FormFieldSize;
  labelColorLight?: boolean;
  spacing?: keyof Theme['spacingSizes'];
  editable?: boolean;
  onEdit?: () => void;
  dotColor?: string;
  dotTooltipText?: string;
  layout?: 'horizontal' | 'vertical';
};

const FormField: FC<PropsWithChildren<Props>> = ({
  children,
  label,
  extraInformation,
  size,
  labelColorLight,
  tooltip,
  spacing = 'sm',
  editable = false,
  onEdit,
  dotColor,
  dotTooltipText,
  layout = 'vertical',
}) => {
  const Wrapper = layout === 'horizontal' ? RowWrapper : ColumnWrapper;
  return (
    <Wrapper
      size={size}
      spacing={spacing}
      as={layout === 'horizontal' ? Row : Column}
    >
      {!!label &&
        (typeof label === 'string' ? (
          <Column spacing="xs">
            <Row centered spacing="xxs">
              <Typography
                color={labelColorLight ? 'grey.600' : undefined}
                variant="h6"
              >
                {label}
              </Typography>
              {tooltip ? (
                <Tooltip title={tooltip}>
                  <InfoOutline fontSize="inherit" />
                </Tooltip>
              ) : null}
              {editable ? (
                <EditButton size="small" onClick={onEdit}>
                  <EditIcon />
                </EditButton>
              ) : null}
              {dotColor ? (
                <Tooltip title={dotTooltipText}>
                  <Dot color={dotColor} />
                </Tooltip>
              ) : null}
            </Row>
            {!!extraInformation && (
              <Typography variant="body2">{extraInformation}</Typography>
            )}
          </Column>
        ) : (
          label
        ))}

      <ChildrenWrapper>{children}</ChildrenWrapper>
    </Wrapper>
  );
};

export default FormField;
